import React, { useCallback } from 'react';
import {
  CascadeSlotPartType,
  CascadeSlotPartData,
  resolvePartData,
} from '../../../../core/types/cascade-slot.types';
import { useSceneStore } from '../../../store/sceneStore';
import { NumberInput } from './NumberInput';
import { ColorPicker } from './ColorPicker';
import { TexturePropertyEditor } from './TexturePropertyEditor';

interface CascadeSlotPartInspectorProps {
  gameObjectId: string;
  componentType: string;
  data: Record<string, unknown>;
}

const TEXT_PARTS: CascadeSlotPartType[] = ['header', 'balance_text', 'win_text', 'win_banner', 'bet_display'];
const BUTTON_PARTS: CascadeSlotPartType[] = [
  'spin_button', 'spin_options', 'buy_feature', 'bet_selector',
  'config_close_btn', 'config_confirm_btn', 'turbo_toggle', 'auto_toggle',
  'bet_close_btn', 'bet_minus_btn', 'bet_plus_btn', 'bet_confirm_btn',
];
const PANEL_PARTS: CascadeSlotPartType[] = ['config_panel', 'config_panel_bg', 'bet_panel', 'bet_panel_bg'];
const TEXTURE_PARTS: CascadeSlotPartType[] = [
  'header', 'spin_button', 'spin_options', 'buy_feature', 'bet_selector',
  'config_close_btn', 'config_confirm_btn', 'turbo_toggle', 'auto_toggle',
  'bet_close_btn', 'bet_minus_btn', 'bet_plus_btn', 'bet_confirm_btn',
  'config_panel_bg', 'bet_panel_bg',
  'balance_text', 'win_text', 'win_banner',
];
const GRID_OPTION_PARTS: CascadeSlotPartType[] = ['auto_count_grid', 'bet_level_grid'];

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider pt-2 pb-1 border-t border-zinc-700 first:border-t-0 first:pt-0">
    {title}
  </div>
);

const LabeledRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-xs text-zinc-500 whitespace-nowrap">{label}</span>
    <div className="flex-1 max-w-[140px]">{children}</div>
  </div>
);

export const CascadeSlotPartInspector: React.FC<CascadeSlotPartInspectorProps> = ({
  gameObjectId,
  componentType,
  data,
}) => {
  const updateComponent = useSceneStore((s) => s.updateComponent);
  const gameObject = useSceneStore((s) => s.gameObjects[gameObjectId]);
  const gameObjects = useSceneStore((s) => s.gameObjects);

  const component = gameObject?.components.find((c) => c.type === componentType);
  const partData = resolvePartData(data);
  const partType = partData.partType;

  /**
   * Map of parent cascade-slot-part properties → child text-renderer properties.
   * When a parent property changes, the corresponding child property is updated.
   */
  const syncToChildren = useCallback(
    (key: keyof CascadeSlotPartData, value: unknown) => {
      if (!gameObject) return;

      // Parent → child text-renderer property mapping
      const PARENT_TO_TEXT_CHILD: Record<string, string> = {
        textColor: 'fill',
        fontColor: 'fill',
        fontSize: 'fontSize',
        fontFamily: 'fontFamily',
        fontWeight: 'fontWeight',
        fontStyle: 'fontStyle',
        textAlign: 'align',
        letterSpacing: 'letterSpacing',
        textContent: 'text',
      };

      const childProp = PARENT_TO_TEXT_CHILD[key];
      if (!childProp) return;

      // Find child text GameObjects and update their text-renderer component
      for (const childId of gameObject.childIds) {
        const child = gameObjects[childId];
        if (!child || child.type !== 'text') continue;
        const textComp = child.components.find((c) => c.type === 'text-renderer');
        if (!textComp) continue;
        updateComponent(childId, textComp.id, { [childProp]: value });
      }
    },
    [gameObject, gameObjects, updateComponent],
  );

  const handleChange = useCallback(
    (key: keyof CascadeSlotPartData, value: unknown) => {
      if (!component) return;
      updateComponent(gameObjectId, component.id, { [key]: value });
      syncToChildren(key, value);
    },
    [gameObjectId, component, updateComponent, syncToChildren]
  );

  const isTextPart = TEXT_PARTS.includes(partType);
  const isButtonPart = BUTTON_PARTS.includes(partType);
  const isGridPart = partType === 'grid';
  const isSymbolPart = partType === 'symbol';

  return (
    <div className="space-y-2">
      <div className="text-xs text-zinc-400">
        Part: <span className="text-zinc-200 font-medium">{partType}</span>
      </div>

      {/* Layout Mode */}
      <SectionHeader title="Layout Mode" />
      <LabeledRow label="Mode">
        <select
          value={partData.layoutMode ?? 'flow'}
          onChange={(e) => handleChange('layoutMode', e.target.value)}
          className="w-full px-2 py-1 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-300 focus:outline-none focus:border-blue-500"
        >
          <option value="flow">Flow</option>
          <option value="free">Free</option>
        </select>
      </LabeledRow>

      {/* Layout section — all parts */}
      <SectionHeader title="Layout" />
      <LabeledRow label="Offset X">
        <NumberInput
          value={partData.offsetX ?? 0}
          onChange={(v) => handleChange('offsetX', v)}
          step={1}
        />
      </LabeledRow>
      <LabeledRow label="Offset Y">
        <NumberInput
          value={partData.offsetY ?? 0}
          onChange={(v) => handleChange('offsetY', v)}
          step={1}
        />
      </LabeledRow>
      <LabeledRow label="Width">
        <NumberInput
          value={partData.customWidth ?? 0}
          onChange={(v) => handleChange('customWidth', v || null)}
          min={0}
          step={1}
        />
      </LabeledRow>
      <LabeledRow label="Height">
        <NumberInput
          value={partData.customHeight ?? 0}
          onChange={(v) => handleChange('customHeight', v || null)}
          min={0}
          step={1}
        />
      </LabeledRow>
      <LabeledRow label="Anchor X">
        <NumberInput
          value={partData.anchorX ?? 0.5}
          onChange={(v) => handleChange('anchorX', v)}
          min={0}
          max={1}
          step={0.1}
        />
      </LabeledRow>
      <LabeledRow label="Anchor Y">
        <NumberInput
          value={partData.anchorY ?? 0.5}
          onChange={(v) => handleChange('anchorY', v)}
          min={0}
          max={1}
          step={0.1}
        />
      </LabeledRow>

      {/* Visibility section — all parts */}
      <SectionHeader title="Visibility" />
      <LabeledRow label="Visible">
        <input
          type="checkbox"
          checked={partData.visible !== false}
          onChange={(e) => handleChange('visible', e.target.checked)}
          className="accent-blue-500"
        />
      </LabeledRow>
      <LabeledRow label="Alpha">
        <NumberInput
          value={partData.alpha ?? 1}
          onChange={(v) => handleChange('alpha', v)}
          min={0}
          max={1}
          step={0.05}
        />
      </LabeledRow>

      {/* Text parts */}
      {isTextPart && (
        <>
          <SectionHeader title="Text Style" />
          <LabeledRow label="Text">
            <input
              type="text"
              value={partData.textContent ?? ''}
              onChange={(e) => handleChange('textContent', e.target.value)}
              placeholder="Auto"
              className="w-full px-2 py-1 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-300 focus:outline-none focus:border-blue-500"
            />
          </LabeledRow>
          <LabeledRow label="Font Size">
            <NumberInput
              value={partData.fontSize ?? 14}
              onChange={(v) => handleChange('fontSize', v)}
              min={6}
              max={72}
              step={1}
            />
          </LabeledRow>
          <LabeledRow label="Font Color">
            <ColorPicker
              value={partData.fontColor ?? '#ffffff'}
              onChange={(v) => handleChange('fontColor', v)}
            />
          </LabeledRow>
          <LabeledRow label="Font Family">
            <input
              type="text"
              value={partData.fontFamily ?? 'Inter'}
              onChange={(e) => handleChange('fontFamily', e.target.value)}
              className="w-full px-2 py-1 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-300 focus:outline-none focus:border-blue-500"
            />
          </LabeledRow>
          <LabeledRow label="Weight">
            <select
              value={partData.fontWeight ?? 'normal'}
              onChange={(e) => handleChange('fontWeight', e.target.value)}
              className="w-full px-2 py-1 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-300 focus:outline-none focus:border-blue-500"
            >
              <option value="normal">Normal</option>
              <option value="bold">Bold</option>
            </select>
          </LabeledRow>
          <LabeledRow label="Style">
            <select
              value={partData.fontStyle ?? 'normal'}
              onChange={(e) => handleChange('fontStyle', e.target.value)}
              className="w-full px-2 py-1 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-300 focus:outline-none focus:border-blue-500"
            >
              <option value="normal">Normal</option>
              <option value="italic">Italic</option>
            </select>
          </LabeledRow>
          <LabeledRow label="Align">
            <select
              value={partData.textAlign ?? 'center'}
              onChange={(e) => handleChange('textAlign', e.target.value)}
              className="w-full px-2 py-1 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-300 focus:outline-none focus:border-blue-500"
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </LabeledRow>
          <LabeledRow label="Spacing">
            <NumberInput
              value={partData.letterSpacing ?? 0}
              onChange={(v) => handleChange('letterSpacing', v)}
              min={-5}
              max={20}
              step={0.5}
            />
          </LabeledRow>
          <LabeledRow label="Bg Color">
            <ColorPicker
              value={partData.bgColor ?? '#27272a'}
              onChange={(v) => handleChange('bgColor', v)}
            />
          </LabeledRow>
        </>
      )}

      {/* Background texture section — header, button parts, panel backgrounds */}
      {TEXTURE_PARTS.includes(partType) && (
        <>
          <SectionHeader title="Background Texture" />
          <div className="space-y-1">
            <span className="text-xs text-zinc-500">Texture</span>
            <TexturePropertyEditor
              value={partData.textureAssetPath ?? null}
              onChange={(path) => handleChange('textureAssetPath', path)}
            />
          </div>
          <LabeledRow label="Fit">
            <select
              value={partData.textureFit ?? 'cover'}
              onChange={(e) => handleChange('textureFit', e.target.value)}
              className="w-full px-2 py-1 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-300 focus:outline-none focus:border-blue-500"
            >
              <option value="fill">Fill</option>
              <option value="contain">Contain</option>
              <option value="cover">Cover</option>
            </select>
          </LabeledRow>
        </>
      )}

      {/* Button parts */}
      {isButtonPart && (
        <>
          <SectionHeader title="Button Style" />
          <LabeledRow label="Label">
            <input
              type="text"
              value={partData.buttonLabel ?? ''}
              onChange={(e) => handleChange('buttonLabel', e.target.value)}
              placeholder="Auto"
              className="w-full px-2 py-1 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-300 focus:outline-none focus:border-blue-500"
            />
          </LabeledRow>
          <LabeledRow label="Bg Color">
            <ColorPicker
              value={partData.bgColor ?? '#2563eb'}
              onChange={(v) => handleChange('bgColor', v)}
            />
          </LabeledRow>
          <LabeledRow label="Text Color">
            <ColorPicker
              value={partData.textColor ?? '#ffffff'}
              onChange={(v) => handleChange('textColor', v)}
            />
          </LabeledRow>
          <LabeledRow label="Font Size">
            <NumberInput
              value={partData.buttonFontSize ?? 12}
              onChange={(v) => handleChange('buttonFontSize', v)}
              min={6}
              max={36}
              step={1}
            />
          </LabeledRow>
          <LabeledRow label="Radius">
            <NumberInput
              value={partData.borderRadius ?? 6}
              onChange={(v) => handleChange('borderRadius', v)}
              min={0}
              max={50}
              step={1}
            />
          </LabeledRow>
          <LabeledRow label="Border Width">
            <NumberInput
              value={partData.borderWidth ?? 0}
              onChange={(v) => handleChange('borderWidth', v)}
              min={0}
              max={10}
              step={1}
            />
          </LabeledRow>
          <LabeledRow label="Border Color">
            <ColorPicker
              value={partData.borderColor ?? '#3f3f46'}
              onChange={(v) => handleChange('borderColor', v)}
            />
          </LabeledRow>
          <LabeledRow label="Padding X">
            <NumberInput
              value={partData.paddingX ?? 0}
              onChange={(v) => handleChange('paddingX', v)}
              min={0}
              max={50}
              step={1}
            />
          </LabeledRow>
          <LabeledRow label="Padding Y">
            <NumberInput
              value={partData.paddingY ?? 0}
              onChange={(v) => handleChange('paddingY', v)}
              min={0}
              max={50}
              step={1}
            />
          </LabeledRow>
        </>
      )}

      {/* Grid part */}
      {isGridPart && (
        <>
          <SectionHeader title="Grid Style" />
          <LabeledRow label="Symbol Scale">
            <NumberInput
              value={partData.symbolScale ?? 1}
              onChange={(v) => handleChange('symbolScale', v)}
              min={0.1}
              max={3}
              step={0.1}
            />
          </LabeledRow>
          <LabeledRow label="Grid Padding">
            <NumberInput
              value={partData.gridPadding ?? 2}
              onChange={(v) => handleChange('gridPadding', v)}
              min={0}
              max={20}
              step={1}
            />
          </LabeledRow>
        </>
      )}

      {/* Symbol part */}
      {isSymbolPart && (
        <>
          <SectionHeader title="Symbol" />
          <LabeledRow label="Symbol ID">
            <input
              type="text"
              value={partData.symbolId ?? ''}
              readOnly
              className="w-full px-2 py-1 text-xs bg-zinc-900 border border-zinc-700 rounded text-zinc-400"
            />
          </LabeledRow>
          <LabeledRow label="Color">
            <ColorPicker
              value={partData.symbolColor ?? '#3B82F6'}
              onChange={(v) => handleChange('symbolColor', v)}
            />
          </LabeledRow>
          <LabeledRow label="Text Color">
            <ColorPicker
              value={partData.symbolTextColor ?? '#ffffff'}
              onChange={(v) => handleChange('symbolTextColor', v)}
            />
          </LabeledRow>
          <LabeledRow label="Row">
            <NumberInput
              value={partData.symbolRow ?? 0}
              onChange={(v) => handleChange('symbolRow', v)}
              min={0}
              step={1}
            />
          </LabeledRow>
          <LabeledRow label="Col">
            <NumberInput
              value={partData.symbolCol ?? 0}
              onChange={(v) => handleChange('symbolCol', v)}
              min={0}
              step={1}
            />
          </LabeledRow>
        </>
      )}

      {/* Panel container parts (config_panel, bet_panel, panel backgrounds) */}
      {PANEL_PARTS.includes(partType) && (
        <>
          <SectionHeader title="Panel Style" />
          <LabeledRow label="Bg Color">
            <ColorPicker
              value={partData.bgColor ?? '#27272a'}
              onChange={(v) => handleChange('bgColor', v)}
            />
          </LabeledRow>
          <LabeledRow label="Radius">
            <NumberInput
              value={partData.borderRadius ?? 12}
              onChange={(v) => handleChange('borderRadius', v)}
              min={0}
              max={50}
              step={1}
            />
          </LabeledRow>
          <LabeledRow label="Border Width">
            <NumberInput
              value={partData.borderWidth ?? 0}
              onChange={(v) => handleChange('borderWidth', v)}
              min={0}
              max={10}
              step={1}
            />
          </LabeledRow>
          <LabeledRow label="Border Color">
            <ColorPicker
              value={partData.borderColor ?? '#3f3f46'}
              onChange={(v) => handleChange('borderColor', v)}
            />
          </LabeledRow>
        </>
      )}

      {/* Grid option parts (auto_count_grid, bet_level_grid) */}
      {GRID_OPTION_PARTS.includes(partType) && (
        <>
          <SectionHeader title="Option Grid Style" />
          <LabeledRow label="Bg Color">
            <ColorPicker
              value={partData.bgColor ?? '#27272a'}
              onChange={(v) => handleChange('bgColor', v)}
            />
          </LabeledRow>
          <LabeledRow label="Radius">
            <NumberInput
              value={partData.borderRadius ?? 6}
              onChange={(v) => handleChange('borderRadius', v)}
              min={0}
              max={30}
              step={1}
            />
          </LabeledRow>
        </>
      )}

      {/* Progress bar part */}
      {partType === 'bet_progress_bar' && (
        <>
          <SectionHeader title="Progress Bar Style" />
          <LabeledRow label="Bar Color">
            <ColorPicker
              value={partData.bgColor ?? '#2563eb'}
              onChange={(v) => handleChange('bgColor', v)}
            />
          </LabeledRow>
        </>
      )}
    </div>
  );
};
